import {
  DimensionSide,
  Element,
  ElementType,
  Group,
  Trapezoid,
} from '@ritabygglov/core';
import { createGroup } from '../services/Actions/Group';
import { SelectionMode, SelectionModes } from '../types';
import {
  computeMultiselectBBox,
  copy,
  getElementAxisAlignedCorners,
  rotateCenter,
  uuid,
} from './utility';

export const isGroup = (element: Element | undefined): element is Group =>
  Boolean(element && element.type === ElementType.Group);

export const findContainingGroupId = (
  elementId: string,
  elements: Record<string, Element>
): string | undefined => {
  const group = Object.values(elements).find(
    (el): el is Group => isGroup(el) && el.childIds.includes(elementId)
  );
  return group?.id;
};

export const expandGroupChildren = (
  group: Group,
  elements: Record<string, Element>
): Element[] =>
  group.childIds
    .map((id) => elements[id])
    .filter((el): el is Element => el !== undefined);

/**
 * A Group's own x/y/width/height is only its bounding box — operating on a
 * Group alone would move, delete or copy the box and leave the (absolutely
 * positioned) children behind. Every selection-wide operation therefore
 * expands a selected Group into the group plus its children.
 */
export const expandGroups = (
  selected: Element[],
  elements: Record<string, Element>
): Element[] =>
  selected.flatMap((el) =>
    isGroup(el) ? [el, ...expandGroupChildren(el, elements)] : [el]
  );

/**
 * Clones a set of elements with fresh ids, offset by the given delta.
 *
 * Crucially, a cloned Group is re-pointed at its *cloned* children via the
 * id map — copying a group and keeping the original childIds would produce a
 * second bbox driving the original elements instead of an independent copy.
 * Child ids with no clone in the set (e.g. a group copied without its
 * children by an older build) are dropped, and a group left with fewer than
 * two children is dropped entirely, matching the auto-ungroup rule.
 *
 * Returns the clones plus the old-id -> new-id map so callers can translate
 * a selection onto the copies.
 */
export const cloneElementsWithNewIds = (
  elements: Element[],
  offsetX: number,
  offsetY: number,
  renderingOrderBase: number
): { elements: Element[]; idMap: Map<string, string> } => {
  const idMap = new Map<string, string>();
  for (const element of elements) {
    idMap.set(element.id, uuid());
  }

  const clones = elements
    .map((element, i): Element | null => {
      const id = idMap.get(element.id) as string;
      const renderingOrder = renderingOrderBase + i;

      if (element.type === ElementType.Polyline) {
        return {
          ...copy(element),
          points: element.points.map((p) => ({
            ...p,
            x: p.x + offsetX,
            y: p.y + offsetY,
          })),
          id,
          renderingOrder,
        };
      }

      if (isGroup(element)) {
        const childIds = element.childIds
          .map((childId) => idMap.get(childId))
          .filter((childId): childId is string => childId !== undefined);
        if (childIds.length < 2) return null;
        return {
          ...copy(element),
          x: element.x + offsetX,
          y: element.y + offsetY,
          id,
          childIds,
          renderingOrder,
        };
      }

      return {
        ...copy(element),
        x: element.x + offsetX,
        y: element.y + offsetY,
        id,
        renderingOrder,
      };
    })
    .filter((el): el is Element => el !== null);

  return { elements: clones, idMap };
};

/**
 * The ids in `elements` that are not a child of some Group in the same set —
 * i.e. what a user would consider selected after pasting or duplicating.
 */
export const topLevelIds = (elements: Element[]): string[] => {
  const childIds = new Set(
    elements.flatMap((el) => (isGroup(el) ? el.childIds : []))
  );
  return elements.filter((el) => !childIds.has(el.id)).map((el) => el.id);
};

/**
 * Whether a selection can be merged into a group: at least two elements,
 * and not already inside an open group (v1 has no nested groups). Shared by
 * the Cmd/Ctrl + G shortcut and the controls panel's group button.
 */
export const canGroupSelection = (
  selectedIds: string[],
  selectionMode: SelectionMode
): boolean =>
  selectedIds.length >= 2 && selectionMode.type !== SelectionModes.GroupEditing;

/**
 * The Group a "ungroup" request would dissolve, or undefined when the
 * current selection isn't a lone group (or the editor is mid-way through
 * another operation). Shared by the Cmd/Ctrl + Shift + G shortcut and the
 * controls panel's ungroup button so the two can't disagree about when
 * ungrouping is offered.
 *
 * Selection mode settles back to None after any completed move/resize/
 * rotate while selectedElements stays populated, so this deliberately does
 * not require SelectionModes.Selected.
 */
export const resolveUngroupTarget = (
  selectedIds: string[],
  elements: Record<string, Element>,
  selectionMode: SelectionMode
): Group | undefined => {
  if (selectedIds.length !== 1) return undefined;
  if (
    selectionMode.type === SelectionModes.GroupEditing ||
    selectionMode.type === SelectionModes.Add ||
    selectionMode.type === SelectionModes.TextEditing
  ) {
    return undefined;
  }
  const only = elements[selectedIds[0]];
  return isGroup(only) ? only : undefined;
};

/**
 * Resolves a selection into the elements a shared bbox transform (resize /
 * rotate) should actually operate on.
 *
 * A Group's own x/y/width/height/rotate is only its bounding box, so every
 * selected Group must be expanded into the group *plus* its children —
 * including when it is selected alongside other elements or other groups.
 * Transforming the bare Group record would spin or stretch the dashed box
 * and leave the real shapes behind.
 *
 * When a single Group is selected its own (authoritative) frame is returned
 * as `group`, so callers can pivot on the group's stored center instead of
 * recomputing a union of already-rotated children, which would drift.
 */
export const resolveGroupTransformTarget = (
  selectedIds: string[],
  elements: Record<string, Element>
): { group?: Group; children: Element[] } => {
  const soleElement =
    selectedIds.length === 1 ? elements[selectedIds[0]] : undefined;
  if (isGroup(soleElement)) {
    return {
      group: soleElement,
      children: expandGroupChildren(soleElement, elements),
    };
  }
  const selected = selectedIds
    .map((id) => elements[id])
    .filter((el): el is Element => el !== undefined);
  return { children: expandGroups(selected, elements) };
};

/**
 * Computes the group's bbox in its own local frame — i.e. the tight
 * axis-aligned union of its children, de-rotated by -group.rotate around
 * the group's own (frozen, since not yet re-saved) center. When
 * group.rotate is 0 this is identical to a plain world-axis-aligned union,
 * so it's safe to use unconditionally in place of a naive union everywhere
 * a group's bbox needs recomputing from its current children (GroupEditing
 * live display, exit-save, delete-child update).
 */
export const computeGroupBBox = (
  group: Group,
  elements: Record<string, Element>
): { x: number; y: number; width: number; height: number } | null => {
  const children = expandGroupChildren(group, elements);
  if (children.length === 0) return null;

  const pivotX = group.x + group.width / 2;
  const pivotY = group.y + group.height / 2;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const child of children) {
    for (const [wx, wy] of getElementAxisAlignedCorners(child)) {
      const [lx, ly] = rotateCenter(wx, wy, pivotX, pivotY, -group.rotate);
      if (lx < minX) minX = lx;
      if (ly < minY) minY = ly;
      if (lx > maxX) maxX = lx;
      if (ly > maxY) maxY = ly;
    }
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

/**
 * Resolves a clicked/hit element id up to its containing group, unless that
 * group is the one currently being edited (activeGroupId), in which case the
 * child id is returned unchanged so it stays individually selectable.
 */
export const resolveSelectionId = (
  elementId: string,
  elements: Record<string, Element>,
  activeGroupId?: string
): string => {
  const groupId = findContainingGroupId(elementId, elements);
  if (!groupId || groupId === activeGroupId) return elementId;
  return groupId;
};

/**
 * Merges a selection of element ids into a single new group. If the
 * selection contains one or more existing groups, their childIds are
 * flattened into the new group and the old group elements are returned for
 * deletion (no nesting in v1).
 */
export const mergeSelectionIntoGroup = (
  selectedIds: string[],
  elements: Record<string, Element>,
  userId: string,
  renderingOrder: number
): { newGroup: Group; mergedGroups: Group[] } => {
  const selectedElements = selectedIds
    .map((id) => elements[id])
    .filter((el): el is Element => el !== undefined);

  const mergedGroups = selectedElements.filter(isGroup);
  const looseIds = selectedElements
    .filter((el) => !isGroup(el))
    .map((el) => el.id);

  const childIds = [
    ...new Set([...looseIds, ...mergedGroups.flatMap((g) => g.childIds)]),
  ];

  const allChildren = childIds
    .map((id) => elements[id])
    .filter((el): el is Element => el !== undefined);

  const bbox = computeMultiselectBBox(allChildren) ?? {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  };

  const newGroup = createGroup(
    bbox.x,
    bbox.y,
    bbox.width,
    bbox.height,
    childIds,
    renderingOrder,
    uuid(),
    userId
  );

  return { newGroup, mergedGroups };
};

/**
 * Double-click on a child of a group (not currently open) enters
 * GroupEditing for it instead of the child's own double-click behavior
 * (e.g. text editing). Returns true if it entered group editing, so callers
 * can skip their type-specific double-click handling.
 */
export const enterGroupEditingIfGrouped = (
  elementId: string,
  elements: Record<string, Element>,
  selectionMode: SelectionMode,
  setSelectionMode: (mode: SelectionMode) => void,
  setSelectedElements: (ids: string[]) => void
): boolean => {
  const groupId = findContainingGroupId(elementId, elements);
  if (!groupId) return false;
  if (
    selectionMode.type === SelectionModes.GroupEditing &&
    selectionMode.groupId === groupId
  ) {
    return false;
  }
  setSelectionMode({ type: SelectionModes.GroupEditing, groupId });
  setSelectedElements([elementId]);
  return true;
};

/**
 * Scales a child within a Group that has its own (possibly non-zero)
 * `rotate` — used when resizing a persisted, rotated Group via a corner/edge
 * handle. The child is carried through the group's resize as a rigid point:
 * de-rotated into the group's original local (unrotated) frame, scaled there
 * exactly like the axis-aligned scaleElementInBbox already does, then
 * re-rotated back into world space. Both steps pivot around the SAME point —
 * the group's *original* center — because a de-rotate/re-rotate round trip
 * only cancels out (and thus keeps unscaled points fixed in world space)
 * when both halves share one pivot.
 *
 * Every non-Polyline element stores x/y as the PRE-rotation reference
 * corner, rendered by rotating it around its OWN center by its OWN `rotate`
 * — a different pivot from the group's. So the corner has to be converted
 * to its actual world position first (rotating around the child's own
 * center), only then de-rotated into the group's frame; and the result has
 * to be converted back from a world point to a pre-rotation x/y afterward,
 * using the child's new (scaled) width/height. Skipping this and treating
 * x/y as already a world point is what let children drift out of the
 * group's own bounding box after a resize. Polyline points, by contrast,
 * are already absolute world coordinates (no separate own-rotation wrapper
 * at render time), so they go straight into the group's frame.
 *
 * width/height (and topWidth/bottomWidth) scale directly — same
 * approximation scaleElementInBbox already makes for a child's own
 * rotation.
 */
export const scaleChildInRotatedGroup = (
  origChild: Element,
  origGroup: { x: number; y: number; rotate: number; cx: number; cy: number },
  scaleX: number,
  scaleY: number,
  newLocalX: number,
  newLocalY: number
): Element => {
  const toGroupLocal = (wx: number, wy: number) =>
    rotateCenter(wx, wy, origGroup.cx, origGroup.cy, -origGroup.rotate);
  const toWorld = (lx: number, ly: number) =>
    rotateCenter(lx, ly, origGroup.cx, origGroup.cy, origGroup.rotate);

  if (origChild.type === ElementType.Polyline) {
    return {
      ...origChild,
      points: origChild.points.map((p) => {
        const [localX, localY] = toGroupLocal(p.x, p.y);
        const scaledLocalX = newLocalX + (localX - origGroup.x) * scaleX;
        const scaledLocalY = newLocalY + (localY - origGroup.y) * scaleY;
        const [worldX, worldY] = toWorld(scaledLocalX, scaledLocalY);
        return { ...p, x: worldX, y: worldY };
      }),
    };
  }

  const childCx = origChild.x + origChild.width / 2;
  const childCy = origChild.y + origChild.height / 2;
  const [worldCornerX, worldCornerY] = rotateCenter(
    origChild.x,
    origChild.y,
    childCx,
    childCy,
    origChild.rotate
  );

  const [localX, localY] = toGroupLocal(worldCornerX, worldCornerY);
  const scaledLocalX = newLocalX + (localX - origGroup.x) * scaleX;
  const scaledLocalY = newLocalY + (localY - origGroup.y) * scaleY;
  const [newWorldCornerX, newWorldCornerY] = toWorld(
    scaledLocalX,
    scaledLocalY
  );

  const newWidth = origChild.width * scaleX;
  const newHeight = origChild.height * scaleY;
  const halfW = newWidth / 2;
  const halfH = newHeight / 2;
  const rotRad = (origChild.rotate * Math.PI) / 180;
  const rotatedHalfX = halfW * Math.cos(rotRad) - halfH * Math.sin(rotRad);
  const rotatedHalfY = halfW * Math.sin(rotRad) + halfH * Math.cos(rotRad);
  const newX = newWorldCornerX - halfW + rotatedHalfX;
  const newY = newWorldCornerY - halfH + rotatedHalfY;

  const scaled: Element = {
    ...origChild,
    x: newX,
    y: newY,
    width: newWidth,
    height: newHeight,
  };
  if (origChild.type === ElementType.Trapezoid) {
    return {
      ...(scaled as Trapezoid),
      topWidth: (origChild as Trapezoid).topWidth * scaleX,
      bottomWidth: (origChild as Trapezoid).bottomWidth * scaleX,
    };
  }
  return scaled;
};

export const rotateElementAroundPivot = (
  origEl: Element,
  pivotX: number,
  pivotY: number,
  deltaAngle: number
): Element => {
  if (origEl.type === ElementType.Polyline) {
    return {
      ...origEl,
      points: origEl.points.map((p) => {
        const [nx, ny] = rotateCenter(p.x, p.y, pivotX, pivotY, deltaAngle);
        return { ...p, x: nx, y: ny };
      }),
    };
  }
  const origCx = origEl.x + origEl.width / 2;
  const origCy = origEl.y + origEl.height / 2;
  const [newCx, newCy] = rotateCenter(
    origCx,
    origCy,
    pivotX,
    pivotY,
    deltaAngle
  );
  return {
    ...origEl,
    x: newCx - origEl.width / 2,
    y: newCy - origEl.height / 2,
    rotate: origEl.rotate + deltaAngle,
  };
};

export const resizeGroup = (
  group: Group,
  children: Element[],
  width: number,
  height: number
): { group: Group; children: Element[] } => {
  if (group.width <= 0 || group.height <= 0 || width <= 0 || height <= 0) {
    return { group, children };
  }

  const cx = group.x + group.width / 2;
  const cy = group.y + group.height / 2;
  const scaleX = width / group.width;
  const scaleY = height / group.height;

  const scaledChildren = children.map((child) =>
    scaleChildInRotatedGroup(
      child,
      { x: group.x, y: group.y, rotate: group.rotate, cx, cy },
      scaleX,
      scaleY,
      group.x,
      group.y
    )
  );

  const [cornerX, cornerY] = rotateCenter(
    group.x,
    group.y,
    cx,
    cy,
    group.rotate
  );
  const [rotatedHalfX, rotatedHalfY] = rotateCenter(
    width / 2,
    height / 2,
    0,
    0,
    group.rotate
  );

  return {
    group: {
      ...group,
      x: cornerX - width / 2 + rotatedHalfX,
      y: cornerY - height / 2 + rotatedHalfY,
      width,
      height,
    },
    children: scaledChildren,
  };
};

export const rotateGroup = (
  group: Group,
  children: Element[],
  rotate: number
): { group: Group; children: Element[] } => {
  const pivotX = group.x + group.width / 2;
  const pivotY = group.y + group.height / 2;
  const delta = rotate - group.rotate;

  return {
    group: { ...group, rotate },
    children: children.map((child) =>
      rotateElementAroundPivot(child, pivotX, pivotY, delta)
    ),
  };
};

export const transformGroup = (
  group: Group,
  children: Element[],
  {
    width = group.width,
    height = group.height,
    rotate = group.rotate,
  }: Partial<Pick<Group, 'width' | 'height' | 'rotate'>>
): { group: Group; children: Element[] } => {
  const resized = resizeGroup(group, children, width, height);
  return rotateGroup(resized.group, resized.children, rotate);
};

export type MirrorAxis = 'horizontal' | 'vertical';

const normalizeAngle = (angle: number) => ((angle % 360) + 360) % 360;

const flipChildLocally = (child: Element, axis: MirrorAxis): Element => {
  switch (child.type) {
    case ElementType.Fixture:
      return axis === 'horizontal'
        ? { ...child, mirroredX: !child.mirroredX }
        : { ...child, mirroredY: !child.mirroredY };
    case ElementType.Triangle:
    case ElementType.Angle:
      return {
        ...child,
        pointRatio: 1 - child.pointRatio,
        rotate:
          axis === 'vertical'
            ? normalizeAngle(child.rotate + 180)
            : child.rotate,
      };
    case ElementType.Trapezoid:
      return axis === 'vertical'
        ? {
            ...child,
            topWidth: child.bottomWidth,
            bottomWidth: child.topWidth,
          }
        : child;
    default:
      return child;
  }
};

export const mirrorGroup = (
  group: Group,
  children: Element[],
  axis: MirrorAxis
): { group: Group; children: Element[] } => {
  const cx = group.x + group.width / 2;
  const cy = group.y + group.height / 2;
  const axisAngle = axis === 'horizontal' ? group.rotate + 90 : group.rotate;

  const reflect = (x: number, y: number) => {
    const [alongX, alongY] = rotateCenter(x, y, cx, cy, -axisAngle);
    return rotateCenter(alongX, 2 * cy - alongY, cx, cy, axisAngle);
  };

  const mirroredChildren = children.map((child): Element => {
    if (child.type === ElementType.Polyline) {
      return {
        ...child,
        points: child.points.map((p) => {
          const [x, y] = reflect(p.x, p.y);
          return {
            ...p,
            x,
            y,
            dimensionSide:
              p.dimensionSide === DimensionSide.Above
                ? DimensionSide.Below
                : DimensionSide.Above,
          };
        }),
      };
    }

    const [centerX, centerY] = reflect(
      child.x + child.width / 2,
      child.y + child.height / 2
    );
    return flipChildLocally(
      {
        ...child,
        x: centerX - child.width / 2,
        y: centerY - child.height / 2,
        rotate: normalizeAngle(2 * group.rotate - child.rotate),
      },
      axis
    );
  });

  return { group, children: mirroredChildren };
};
